import React, { useEffect, useState } from "react";

const STORAGE_KEY = "excel-cert-application";
const PAYMENT_BRIDGE_URL = process.env.REACT_APP_RU_PAYMENT_BRIDGE_URL || "https://payments.africaresearchuniversity.com/react_cart.php";

const LEVEL_PRICING = {
  "Beginner Level - K1000": 1000,
  "Intermediate Level - K1000": 1000,
  "Both Levels - K1500": 1500,
};

const COUNTRY_OPTIONS = [
  { name: "Zambia", phoneCode: "+260" },
  { name: "Zimbabwe", phoneCode: "+263" },
  { name: "South Africa", phoneCode: "+27" },
  { name: "Botswana", phoneCode: "+267" },
  { name: "Namibia", phoneCode: "+264" },
  { name: "Malawi", phoneCode: "+265" },
  { name: "Tanzania", phoneCode: "+255" },
  { name: "Kenya", phoneCode: "+254" },
  { name: "Uganda", phoneCode: "+256" },
  { name: "Rwanda", phoneCode: "+250" },
  { name: "Nigeria", phoneCode: "+234" },
  { name: "Ghana", phoneCode: "+233" },
  { name: "Ethiopia", phoneCode: "+251" },
  { name: "Egypt", phoneCode: "+20" },
  { name: "Morocco", phoneCode: "+212" },
  { name: "United Kingdom", phoneCode: "+44" },
  { name: "United States", phoneCode: "+1" },
  { name: "Canada", phoneCode: "+1" },
  { name: "India", phoneCode: "+91" },
  { name: "China", phoneCode: "+86" },
  { name: "Australia", phoneCode: "+61" },
  { name: "Other", phoneCode: "" },
];

const ZAMBIAN_PROVINCES = [
  "Central",
  "Copperbelt",
  "Eastern",
  "Luapula",
  "Lusaka",
  "Muchinga",
  "Northern",
  "North-Western",
  "Southern",
  "Western",
];

const sections = [
  {
    id: 1,
    title: "Personal Information",
    fields: [
      { id: "fullName", label: "Full Name", type: "text", required: true },
      { id: "phone", label: "Phone Number (Without Country Code)", type: "text", required: true, hint: "E.g., 977123456" },
      { id: "whatsapp", label: "WhatsApp Number", type: "text", required: true },
      { id: "email", label: "Email Address", type: "email", required: true },
      { id: "country", label: "Country of Residence", type: "select", required: true, options: COUNTRY_OPTIONS.map((country) => country.name) },
      { id: "occupation", label: "Current Occupation / Job Title", type: "text", required: true },
    ],
  },
  {
    id: 2,
    title: "Programme Details",
    fields: [
      {
        id: "level",
        label: "Level of Excel Knowledge Applying For",
        type: "radio",
        required: true,
        options: ["Beginner Level - K1000", "Intermediate Level - K1000", "Both Levels - K1500"],
      },
      { id: "reason", label: "Reason for Enrolling in This Programme", type: "text", required: true, hint: "Short response" },
    ],
  },
  {
    id: 3,
    title: "Learning & Access Confirmation",
    fields: [
      {
        id: "device",
        label: "Do you have access to a smartphone or computer with internet?",
        type: "radio",
        required: true,
        options: ["No", "Yes"],
      },
      {
        id: "referral",
        label: "How did you hear about this?",
        type: "radio",
        required: true,
        options: ["Facebook", "LinkedIn", "WhatsApp Channel", "Website", "Friend"],
      },
    ],
  },
  {
    id: 4,
    title: "Billing Details",
    fields: [
      { id: "address", label: "Street Address", type: "text", required: true },
      { id: "address2", label: "Apartment, Suite, Unit, etc.", type: "text", required: false, hint: "Optional" },
      { id: "city", label: "Town / City", type: "text", required: true },
      { id: "state", label: "Province / State", type: "select", required: true, options: ZAMBIAN_PROVINCES },
      { id: "zipCode", label: "Postcode / ZIP", type: "text", required: true },
      { id: "passport", label: "Passport / ID Number", type: "text", required: false, hint: "Optional" },
      { id: "companyName", label: "Company Name", type: "text", required: false, hint: "Optional" },
      { id: "orderNotes", label: "Order Notes", type: "textarea", required: false, hint: "Optional" },
    ],
  },
];

const initialForm = {
  fullName: "",
  phone: "",
  whatsapp: "",
  email: "",
  country: "Zambia",
  occupation: "",
  level: "Beginner Level - K1000",
  reason: "",
  device: "Yes",
  referral: "Website",
  address: "",
  address2: "",
  city: "",
  state: "Lusaka",
  zipCode: "",
  passport: "",
  companyName: "",
  orderNotes: "",
};

function readSession() {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

function formatMoney(amount) {
  return `K${amount.toLocaleString()}`;
}

function getPhoneCodeForCountry(countryName) {
  return COUNTRY_OPTIONS.find((country) => country.name === countryName)?.phoneCode || "";
}

function getProgrammeName(level) {
  return String(level || "").split(" - ")[0] || "Not selected";
}

function splitName(fullName) {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" ") || "Applicant",
  };
}

function normalizeCountryCode(countryCode) {
  const digits = String(countryCode || "").replace(/[^\d]/g, "");
  return digits ? `+${digits}` : "";
}

function buildPaymentFields(form, amount) {
  const { firstName, lastName } = splitName(form.fullName);
  const countryCode = normalizeCountryCode(getPhoneCodeForCountry(form.country));

  return {
    first_name: firstName,
    last_name: lastName,
    email: form.email,
    company_name: form.companyName,
    passport: form.passport,
    country_code: countryCode || "+260",
    phone: form.phone,
    address: form.address,
    address2: form.address2,
    city: form.city,
    state: form.state,
    zip_code: form.zipCode,
    country: form.country,
    order_notes: form.orderNotes,
    full_name: form.fullName,
    whatsapp: form.whatsapp,
    occupation: form.occupation,
    level: form.level,
    reason: form.reason,
    device: form.device,
    referral: form.referral,
    fee_name: `Professional Microsoft Excel Certificate Programme - ${form.level}`,
    option_id: "excel-cert-programme",
    quantity: "1",
    amount: String(amount),
    application_json: JSON.stringify({
      fullName: form.fullName,
      whatsapp: form.whatsapp,
      occupation: form.occupation,
      level: form.level,
      reason: form.reason,
      device: form.device,
      referral: form.referral,
    }),
  };
}

function submitPaymentForm(fields) {
  const formElement = document.createElement("form");
  formElement.method = "POST";
  formElement.action = PAYMENT_BRIDGE_URL;

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value ?? "";
    formElement.appendChild(input);
  });

  document.body.appendChild(formElement);
  formElement.submit();
}

function TextInput({ field, value, onChange }) {
  return (
    <input
      type={field.type}
      value={value}
      onChange={(event) => onChange(field.id, event.target.value)}
      placeholder="Enter your answer"
      className="w-full rounded-2xl border border-emerald-200 bg-emerald-50/50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:bg-white"
    />
  );
}

function TextAreaInput({ field, value, onChange }) {
  return (
    <textarea
      rows="4"
      value={value}
      onChange={(event) => onChange(field.id, event.target.value)}
      placeholder="Enter your answer"
      className="w-full rounded-2xl border border-emerald-200 bg-emerald-50/50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:bg-white"
    />
  );
}

function SelectInput({ field, value, onChange }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(field.id, event.target.value)}
      className="w-full rounded-2xl border border-emerald-200 bg-emerald-50/50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:bg-white"
    >
      <option value="">Select an option</option>
      {field.options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function RadioInput({ field, value, onChange }) {
  return (
    <div className="grid gap-3">
      {field.options.map((option) => {
        const checked = value === option;
        return (
          <label
            key={option}
            className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm transition ${checked ? "border-emerald-600 bg-emerald-50 text-emerald-950" : "border-emerald-100 bg-white text-slate-700 hover:border-emerald-300"
              }`}
          >
            <input type="radio" name={field.id} checked={checked} onChange={() => onChange(field.id, option)} className="h-4 w-4 accent-emerald-700" />
            <span>{option}</span>
          </label>
        );
      })}
    </div>
  );
}

function SummaryRow({ label, value, strong = false }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-emerald-100 py-3 last:border-b-0">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <span className={`text-right text-sm ${strong ? "font-bold text-emerald-950" : "font-semibold text-slate-900"}`}>{value}</span>
    </div>
  );
}

const ExcelCertificatePage = () => {
  const stored = readSession();
  const [form, setForm] = useState(stored?.form || initialForm);
  const [errors, setErrors] = useState({});
  const [paymentError, setPaymentError] = useState("");
  const [processing, setProcessing] = useState(false);

  const amount = LEVEL_PRICING[form.level] || 0;

  useEffect(() => {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ form }));
  }, [form]);

  const handleChange = (id, value) => {
    setForm((current) => ({ ...current, [id]: value }));
    setErrors((current) => ({ ...current, [id]: "" }));
    setPaymentError("");
  };

  const validate = () => {
    const nextErrors = {};
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.required && !String(form[field.id] || "").trim()) {
          nextErrors[field.id] = "This field is required.";
        }
      });
    });

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!splitName(form.fullName).firstName) {
      nextErrors.fullName = "Enter your full name.";
    }

    return nextErrors;
  };

  const handleSubmit = () => {
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      document.querySelector(".field-error")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setProcessing(true);
    submitPaymentForm(buildPaymentFields(form, amount));
  };

  const handleReset = () => {
    setForm(initialForm);
    setErrors({});
    setPaymentError("");
    setProcessing(false);
    window.sessionStorage.removeItem(STORAGE_KEY);
    window.history.replaceState({}, "", window.location.pathname);
  };

  let fieldNumber = 0;
  const derivedCountryCode = getPhoneCodeForCountry(form.country) || "+260";

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.14),_transparent_28%),linear-gradient(135deg,_#f4fbf7_0%,_#ebf6ef_50%,_#f9fcfa_100%)] px-4 py-8 text-left sm:px-6 lg:px-8">
      <div className="mx-auto mb-8 max-w-3xl overflow-hidden rounded-[2rem] bg-[linear-gradient(145deg,_#14532d_0%,_#0f3d22_100%)] p-8 text-white shadow-[0_30px_80px_rgba(6,78,59,0.28)] sm:p-10">
        <div className="inline-flex rounded-3xl border border-white/15 bg-white px-4 py-3 shadow-[0_16px_36px_rgba(0,0,0,0.12)]">
          <img src="/assets/img/arulogo.png" alt="Africa Research University" className="h-16 w-auto sm:h-20" />
        </div>
        <h1 className="mt-6 font-serif text-4xl leading-tight sm:text-5xl">Professional Microsoft Excel</h1>
        <h2 className="mt-2 font-serif text-xl italic text-emerald-50/85 sm:text-2xl">Certificate Programme</h2>
        <p className="mt-4 max-w-xl text-sm leading-6 text-emerald-50/75 sm:text-base">Beginner and Intermediate Level | Fully Online</p>
      </div>

      <div className="mx-auto max-w-3xl overflow-hidden rounded-[2rem] border border-emerald-100 bg-white/95 shadow-[0_30px_80px_rgba(6,78,59,0.12)]">
        {sections.map((section) => (
          <div key={section.id} className="border-b border-emerald-100 px-6 py-6 last:border-b-0 sm:px-8">
            <div className="-mx-6 mb-4 border-l-4 border-emerald-700 bg-emerald-50 px-6 py-4 sm:-mx-8 sm:px-8">
              <span className="block text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-700">Section {section.id}</span>
              <h3 className="mt-1 font-serif text-2xl text-emerald-950">{section.title}</h3>
            </div>
            {section.fields.map((field) => {
              fieldNumber += 1;
              const hasError = Boolean(errors[field.id]);
              return (
                <div key={field.id} className={`rounded-3xl px-4 py-4 ${hasError ? "field-error bg-rose-50/70" : ""}`}>
                  <label className="mb-3 block text-sm font-semibold leading-6 text-slate-800">
                    <span className="mr-2 text-emerald-700">{fieldNumber}.</span>
                    {field.label}
                    {field.required ? <span className="text-rose-600"> *</span> : null}
                    {field.hint ? <span className="text-xs font-normal text-slate-500"> ({field.hint})</span> : null}
                  </label>
                  {field.type === "radio" ? <RadioInput field={field} value={form[field.id]} onChange={handleChange} /> : null}
                  {field.type === "select" ? <SelectInput field={field} value={form[field.id]} onChange={handleChange} /> : null}
                  {field.type === "textarea" ? <TextAreaInput field={field} value={form[field.id]} onChange={handleChange} /> : null}
                  {!["radio", "select", "textarea"].includes(field.type) ? <TextInput field={field} value={form[field.id]} onChange={handleChange} /> : null}
                  {hasError ? <p className="mt-2 text-xs font-medium text-rose-700">{errors[field.id]}</p> : null}
                </div>
              );
            })}
          </div>
        ))}
        {paymentError ? (
          <div className="px-6 pt-6 sm:px-8">
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{paymentError}</div>
          </div>
        ) : null}
        <div className="px-6 pb-8 pt-6 sm:px-8">
          <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="sm:col-span-2 lg:col-span-3 rounded-[1.75rem] border border-emerald-100 bg-[linear-gradient(180deg,_#ffffff_0%,_#f7fbf8_100%)] p-6 shadow-[0_16px_40px_rgba(6,78,59,0.08)]">
              <div className="flex flex-wrap items-start justify-between gap-4 border-b border-emerald-100 pb-5">
                <div>
                  <span className="block text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-700">Application Review</span>
                  <h3 className="mt-2 font-serif text-2xl text-emerald-950">Payment Summary</h3>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                    Please review your application details and the amount to be paid before submitting.
                  </p>
                </div>
              </div>

              <div className="mt-4 grid gap-8 lg:grid-cols-2">
                <div>
                  <SummaryRow label="Applicant" value={form.fullName || "Not provided"} />
                  <SummaryRow label="Programme" value={getProgrammeName(form.level)} />
                  <SummaryRow label="Email" value={form.email || "Not provided"} />
                  <SummaryRow label="Phone" value={`${derivedCountryCode} ${form.phone}`.trim() || "Not provided"} />
                </div>
                <div>
                  <SummaryRow label="Country" value={form.country || "Not selected"} />
                  <SummaryRow label="Town / City" value={form.city || "Not provided"} />
                  <SummaryRow label="Occupation" value={form.occupation || "Not provided"} />
                  <SummaryRow label="Amount" value={amount ? formatMoney(amount) : "K0"} strong />
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              className="rounded-full bg-[linear-gradient(135deg,_#15803d_0%,_#14532d_100%)] px-6 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-[0_18px_36px_rgba(21,128,61,0.25)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleSubmit}
              disabled={processing}
            >
              {processing ? "Opening Cart..." : "Submit Application"}
            </button>
            <button
              className="rounded-full border border-slate-300 px-5 py-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              onClick={handleReset}
              disabled={processing}
            >
              Reset Form
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelCertificatePage;
